import { Component } from '@theme/component';

/**
 * @typedef {Object} ShowMoreRefs
 * @property {HTMLElement[]} tabCaptions - The button to toggle visibility of the items
 * @property {HTMLElement[]} tabContents - The hidden items to show and hide
 */

/**
 * A custom element that manages the showing and hiding excess content items
 *
 * @extends {Component<ShowMoreRefs>}
 */

class TabsComponent extends Component {
  /**
   * @type {'hidden'}
   */
  #disabledClass = 'hidden';
  /**
   *
   * @type {'active'}
   */
  #activeClass = 'active';
  /** @type {HTMLElement[] | []} */
  #tabCaptions = [];
  /** @type {HTMLElement[] | []} */
  #tabContents = [];
  connectedCallback() {
    super.connectedCallback();
    this.#init();
    this.#handleObserve();
  }

  #handleObserve() {
    document.addEventListener('click', (event) => {
      const target = event.target.closest('a');
      if (!target) return;
      const searchParams = new URLSearchParams(target.search);

      if (target.pathname === window.location.pathname && searchParams.has('tab')) {
        const tab = searchParams.get('tab');
        if (!tab) return;

        const button = this.querySelector(`[data-tab="${tab}"]`);
        console.log('button', button);
        if (button) {
          event.preventDefault();
          button.click();
          this.#scrollTo();
          return;
        }
      }

      const button = this.querySelector(target.getAttribute('href'));
      if (button) {
        event.preventDefault();
        button?.click();
        this.#scrollTo();
      }
    });
  }

  #init() {
    this.#tabCaptions = this.refs?.tabCaptions ?? [];
    this.#tabContents = this.refs?.tabContents ?? [];
    let params = new URLSearchParams(document.location.search);
    let tab = params.get('tab');
    if (!tab) return;
    // @ts-ignore
    const button = this.querySelector(`[data-tab="${tab}"]`);
    if (!button) return;
    button.click();

    this.#scrollTo();

    setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('tab');
      window.history.pushState({}, document.title, url.toString());
    }, 1000);
  }

  #scrollTo() {
    const top = this.closest('.shopify-section')?.getBoundingClientRect().top;
    if (top) {
      const headerHeight = document.querySelector('#header-component[sticky="always"]')?.clientHeight || 0;
      window.scrollTo({ top: top + window.scrollY - headerHeight, behavior: 'smooth' });
    }
  }

  /**
   * Toggles the expansion state of the content.
   *
   * @param {Event} event - The click event
   */
  toggle = (event) => {
    event.preventDefault();

    const caption = event.target;
    if (!(caption instanceof HTMLButtonElement)) return;
    const id = caption.dataset.id;

    this.#tabCaptions.forEach((element) => {
      if (element.dataset.id === id) {
        element.classList.add(this.#activeClass);
      } else {
        element.classList.remove(this.#activeClass);
      }
    });
    this.#tabContents.forEach((element) => {
      if (element.dataset.id === id) {
        element.classList.remove(this.#disabledClass);
      } else {
        element.classList.add(this.#disabledClass);
      }
    });
  };
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
